<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import type { CombinedError } from '@urql/core';

const props = withDefaults(defineProps<{
  error: CombinedError,
  class?: HTMLAttributes['class'],
  focus?: boolean,
}>(), {
  focus: true,
});

const errors = computed(() => {
  const rawErrors = toRaw(props.error);
  return rawErrors?.graphQLErrors && rawErrors.graphQLErrors.length > 0
    ? rawErrors.graphQLErrors[0]
    : undefined;
});

const messageError = computed(() => ((errors.value?.extensions && 'debugMessage' in errors.value.extensions)
  ? `${errors.value.message} (${errors.value.extensions.debugMessage})`
  : errors.value?.message
));
const validationError = computed(
  () => errors.value?.extensions?.validation as Record<string, string[]> | undefined,
);

const validationMessages = computed(() => {
  if (!validationError.value) {
    return [];
  }
  return Object.values(validationError.value).flat();
});

// Focus if the error is visible and the focus prop is true
const errorId = computed(() => `error-${useId()}`);
const onFocus = () => {
  if (props.focus) {
    document.getElementById(errorId.value)?.focus();
  }
};
watch(() => props.focus, onFocus);
onMounted(onFocus);
</script>

<template>
  <slot :validation-error="validationError" :message-error="messageError" :error-id="errorId">
    <UAlert
      :id="errorId"
      color="error"
      variant="subtle"
      :class="$props.class"
      tabindex="-1"
      role="alert"
      data-cy="cy-error-alert"
    >
      <template #leading>
        <UiFaIcon
          :icon="['fal', 'circle-exclamation']"
          class="pt-0.5"
          aria-hidden="true"
        />
      </template>
      <template #title>
        <template v-if="validationMessages.length === 1">
          Please review the error
        </template>
        <template v-else-if="validationMessages.length">
          Please review the errors
        </template>
        <div v-else>
          {{ messageError }}
        </div>
      </template>
      <template
        v-if="validationError && validationMessages.length"
        #description
      >
        <UiErrorValidation :errors="validationError" />
      </template>
    </UAlert>
  </slot>
</template>
